import { Component, OnInit } from '@angular/core';
import { OrderService } from '../../order.service';
import { IOrder } from '../../../model/interfaces/order';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicField, DynamicFieldType, DynamicFieldTypes, IDatepickerOptions, InputTypes } from '@mt-ng2/dynamic-form';
import { PickupService } from '../../../model/shared-entities/pickups/pickup.service';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { IModalResult, ModalService } from '@mt-ng2/modal-module';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { IPickup } from '../../../model/interfaces/pickup';
import { CommonFunctions } from '../../../common/services/common-functions.service';
import { DriverTimeSlot } from '../../../model/classes/driver-time-slot';
import { PhoneFormatPipe } from '../../../common/pipes/phone-format';
import { CommonService } from '../../../common/services/common.service';
import { PickupStatus } from '../../order-basic-info/pickup-status-enum';
import { concatMap } from 'rxjs/operators';
import { IDriverDTO } from '../../../model/interfaces/custom/driver-dto';
import { Observable } from 'rxjs';
import { IEntitySearchParams } from '@mt-ng2/common-classes';
import { ICreditEligibility, IProcessPickupCredit } from '../../../model/interfaces/pickup-credit';
import { ISelectionChangedEvent as ITypeAheadSelectionChangedEvent, VirtualTypeAheadGetItemsFunction } from '@mt-ng2/type-ahead-control';

@Component({
    selector: 'app-pickup-scheduling-driver-selection',
    templateUrl: './driver-selection.component.html',
    styleUrls: ['./driver-selection.component.css'],
    standalone: false
})
export class PickupSchedulingDriverSelectionComponent implements OnInit {
    selectedPickup: IPickup;
    selectedOrders: IOrder[];
    drivers: IDriverDTO[];
    driverSelectionForm: FormGroup = null;
    selectedDriverTouched = false;
    selectedDriver: IDriverDTO;
    selectedDateTime: string;
    createdPickupId: number;
    remainingBalance: number;
    isOutsidePrePaymentTimeframe: boolean;
    availableDateTimes: DriverTimeSlot[] = [];
    startDate = new Date();
    endDate = new Date();
    today = new Date();
    phoneFormatPipe: PhoneFormatPipe;
    doubleClickIsDisabled = false;
    dateRequestedField: DynamicField;
    getItems: VirtualTypeAheadGetItemsFunction = this.getDrivers.bind(this);
    datepickerOptions: IDatepickerOptions = {
        minDate: {
            year: this.today.getFullYear(),
            month: this.today.getMonth() + 1,
            day: this.today.getDate(),
        },
    };

    constructor(
        private orderService: OrderService,
        private pickupService: PickupService,
        private notificationsService: NotificationsService,
        private modalService: ModalService,
        private router: Router,
        private fb: FormBuilder,
        private commonService: CommonService,
    ) {}

    ngOnInit(): void {
        this.phoneFormatPipe = new PhoneFormatPipe();
        this.orderService.selectedPickupOrders$.subscribe((selectedOrders) => {
            if (!selectedOrders || selectedOrders.length <= 0) {
                void this.router.navigate([`/create-requests/order-selection`]);
                return;
            }

            this.selectedOrders = selectedOrders;
            this.pickupService.selectedPickup$.subscribe((selectedPickup) => {
                if (selectedPickup) {
                    this.selectedPickup = selectedPickup;
                    if (this.selectedPickup.DriverId > 0 || this.selectedPickup.DriverId != null) {
                        this.pickupService.getPickupDriverById(this.selectedPickup.DriverId).subscribe((driver) => {
                            this.selectedDriver = driver;
                        });
                    }
                    this.selectedDateTime = this.selectedPickup.DateRequested?.toString();
                    this.startDate = new Date(this.selectedPickup.DateRequested);
                    this.endDate = new Date(this.selectedPickup.DateRequested);
                }

                const rawValue = this.selectedPickup?.DateRequested ?? this.today;
                const value = rawValue instanceof Date ? rawValue : new Date(rawValue);
                this.dateRequestedField = new DynamicField({
                    formGroup: '',
                    label: '',
                    labelHtml: `<i class="fa fa-lg fa-fw fa-calendar"></i> <b>Select Date</b>:`,
                    name: 'dateRequested',
                    type: new DynamicFieldType({
                        fieldType: DynamicFieldTypes.Input,
                        inputType: InputTypes.Datepicker,
                        datepickerOptions: this.datepickerOptions,
                    }),
                    validators: { required: true },
                    value: value,
                });

                this.driverSelectionForm = this.fb.group({
                    deliveryZipCode: [this.selectedPickup?.DeliveryZipCode ?? '', [Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$')]],
                    dateRequested: new FormControl(this.dateRequestedField.value, [Validators.required]),
                });
            });
        });
    }

    getDrivers(searchText: string): Observable<IDriverDTO[]> {
        const searchEntity: IEntitySearchParams = {
            query: searchText,
        };
        return this.pickupService.getPickupDriversByName(searchEntity);
    }

    onDriverSelected(event: ITypeAheadSelectionChangedEvent): void {
        this.selectedDriverTouched = true;
        this.selectedDriver = event.selection;
        this.getSearchTimes();
    }

    startDateChange(event: Date): void {
        this.startDate = event;
        this.endDate = event;
        this.getSearchTimes();
    }

    onDriverSelectionFormSubmit(): void {
        if (this.selectedPickup?.Id > 0) {
            this.updateExistingPickup();
        } else {
            this.createNewPickup();
        }
    }

    onDateTimeSelected(dateTime: DriverTimeSlot): void {
        if (this.isSlotFull(dateTime)) return;

        this.selectedDateTime = dateTime.TimeSlot?.toString() ?? '';
    }

    isSlotFull(slot: DriverTimeSlot): boolean {
        return !slot || slot.NumberOfSlots <= 0;
    }

    submitButtonShouldBeDisabled(): boolean {
        if (!this.selectedDateTime || this.driverSelectionForm.controls.deliveryZipCode.invalid || this.doubleClickIsDisabled) return true;

        return !this.availableDateTimes.find((time) => time.TimeSlot.toString() === this.selectedDateTime && !this.isSlotFull(time));
    }

    cancelPickup(pickup: IPickup): void {
        this.commonService.cancelPickup(pickup);
    }

    private createNewPickup(): void {
        const params = {
            driverId: this.selectedDriver?.Id,
            dateRequested: CommonFunctions.getUTCEquivalent(this.selectedDateTime),
            dateConfirmed: null,
            deliveryZipCode: this.driverSelectionForm.controls.deliveryZipCode.value as string,
            orders: this.selectedOrders,
            statusId: PickupStatus.Requested,
            carrierId: this.selectedOrders[0].CarrierId,
        };

        this.pickupService
            .createScheduledPickup(params)
            .pipe(
                concatMap((pickup: IPickup) => {
                    this.createdPickupId = pickup.Id;
                    return this.pickupService.sendUpdatedEmails(pickup.Id);
                }),
                concatMap(() => this.pickupService.getRemainingBalance(this.createdPickupId)),
                concatMap((remainingBalance) => {
                    this.remainingBalance = remainingBalance;
                    return this.pickupService.isOutsidePrePaymentTimeframe(this.createdPickupId);
                }),
            )
            .subscribe({
                next: (isOutsidePrePaymentTimeframe) => {
                    this.isOutsidePrePaymentTimeframe = isOutsidePrePaymentTimeframe;
                    this.showSuccessModal(this.createdPickupId);
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.handleErrorResponse(errorResponse);
                },
            });
    }

    private updateExistingPickup(): void {
        this.selectedPickup.Orders = this.selectedOrders;
        this.selectedPickup.DriverId = this.selectedDriver?.Id;
        this.selectedPickup.DateRequested = CommonFunctions.getUTCEquivalent(this.selectedDateTime);
        this.selectedPickup.DeliveryZipCode = this.driverSelectionForm.controls.deliveryZipCode.value as string;

        this.pickupService
            .updateWithFks(this.selectedPickup)
            .pipe(
                concatMap(() => {
                    return this.pickupService.sendUpdatedEmails(this.selectedPickup.Id);
                }),
                concatMap(() => this.pickupService.getRemainingBalance(this.selectedPickup.Id)),
                concatMap((remainingBalance) => {
                    this.remainingBalance = remainingBalance;
                    return this.pickupService.isOutsidePrePaymentTimeframe(this.selectedPickup.Id);
                }),
            )
            .subscribe({
                next: (isOutsidePrePaymentTimeframe) => {
                    this.isOutsidePrePaymentTimeframe = isOutsidePrePaymentTimeframe;
                    this.showSuccessModal(this.selectedPickup.Id);
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.handleErrorResponse(errorResponse);
                },
            });
    }

    private getSearchTimes() {
        this.pickupService
            .getDriverSearchTimes(
                this.selectedOrders.map((o) => o.Id),
                CommonFunctions.setToMidnight(this.startDate),
                CommonFunctions.setToMidnight(this.endDate),
                this.selectedOrders[0].CustomerId,
                this.selectedOrders[0].Warehouse?.OfficeId,
                this.selectedDriver?.Id ?? 0,
            )
            .subscribe((availableDateTimes) => {
                this.availableDateTimes = availableDateTimes;
                const selectedSlot = this.availableDateTimes.find((time) => time.TimeSlot.toString() === this.selectedDateTime);
                if (selectedSlot && this.isSlotFull(selectedSlot)) {
                    this.selectedDateTime = '';
                }
            });
    }

    private showSuccessModal(pickupId: number): void {
        const allowPrePayment = this.remainingBalance > 0 && !this.isOutsidePrePaymentTimeframe && this.selectedDriver?.Id > 0;

        if (allowPrePayment) {
            const html = `
                <p>Our team is reviewing your request and will confirm your pickup date and time ASAP.</p>
                <p>There is a balance of $${this.remainingBalance.toFixed(2)} for driver ${this.selectedDriver['Name']} to pay.</p>
                <p>Do you want to pay in advance for the pickup?</p>
            `;

            this.modalService
                .showModal({
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    html: html,
                    title: 'Got It!',
                    icon: 'success',
                    showConfirmButton: true,
                    showCancelButton: true,
                })
                .subscribe((result: IModalResult) => {
                    if (result.isConfirmed) {
                        this.pickupService.checkCarrierCreditEligibility(pickupId).subscribe({
                            next: (creditEligibility: ICreditEligibility) => {
                                // If carrier has sufficient credit, process with credit
                                if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= this.remainingBalance) {
                                    this.processPickupWithCredit(pickupId);
                                    return;
                                }

                                void this.router.navigate([`pickups/${pickupId}/pre-payment`]);
                            },
                            error: () => {
                                void this.router.navigate([`pickups/${pickupId}/pre-payment`]);
                            },
                        });
                        return;
                    }

                    void this.router.navigate(['create-requests/manage-pickups']);
                });
            return;
        }

        // Show success message - different text based on whether driver is assigned
        const successText =
            this.selectedDriver?.Id > 0
                ? 'Our team is reviewing your request and will confirm your pickup date and time ASAP.'
                : 'Your pickup request has been submitted. Since no driver was assigned, a driver will be assigned later and pre-payment is not available at this time.';

        this.modalService
            .showModal({
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                text: successText,
                title: 'Got It!',
                icon: 'success',
            })
            .subscribe(() => {
                void this.router.navigate(['create-requests/manage-pickups']);
            });
    }

    private processPickupWithCredit(pickupId: number): void {
        this.pickupService.processPickupWithCredit(pickupId).subscribe({
            next: (result: IProcessPickupCredit) => {
                if (result.Success) {
                    this.notificationsService.success('Payment processed successfully using carrier credit.');
                    // Navigate back to manage pickups
                    void this.router.navigate(['create-requests/manage-pickups']);
                } else {
                    this.notificationsService.error('Failed to process payment with credit. Opening payment screen.');
                    void this.router.navigate([`pickups/${pickupId}/pre-payment`]);
                }
            },
            error: () => {
                this.notificationsService.error('Error processing payment with credit. Opening payment screen.');
                void this.router.navigate([`pickups/${pickupId}/pre-payment`]);
            },
        });
    }

    private handleErrorResponse(errorResponse: HttpErrorResponse): void {
        if (typeof errorResponse.error === 'string') {
            this.notificationsService.error(errorResponse.error);
            return;
        }
        const modelStateError = errorResponse.error?.['ModelState']?.[0] as string;
        this.notificationsService.error(modelStateError ?? 'An error occurred. Please try again.');
    }
}
